/**
 * Feature Flag Editor Dialog
 */

import { useState, useEffect } from 'react';
import { X, Plus, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import type { FeatureFlag } from '@/types/featureFlags';

interface FlagEditorProps {
  flagName?: string;
  flagConfig?: FeatureFlag;
  existingFlags: string[];
  onSave: (flagName: string, config: FeatureFlag, isNew: boolean) => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

const PLATFORMS = ['ios', 'android', 'web'] as const;
const REGIONS = ['cn', 'global'] as const;

export function FlagEditor({
  flagName,
  flagConfig,
  existingFlags,
  onSave,
  onClose,
  isSubmitting,
}: FlagEditorProps) {
  const { t } = useI18n();
  const isEditing = !!flagName;

  const [name, setName] = useState(flagName || '');
  const [enabled, setEnabled] = useState(flagConfig?.enabled ?? false);
  const [description, setDescription] = useState(flagConfig?.description || '');
  const [platforms, setPlatforms] = useState<string[]>(
    flagConfig?.platforms || ['ios', 'android', 'web']
  );
  const [regions, setRegions] = useState<string[]>(
    flagConfig?.regions || []
  );
  const [rolloutPercentage, setRolloutPercentage] = useState(
    flagConfig?.rollout_percentage ?? 100
  );
  const [userGroups, setUserGroups] = useState<string[]>(
    flagConfig?.user_groups || []
  );
  const [newGroupInput, setNewGroupInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (flagName && flagConfig) {
      setName(flagName);
      setEnabled(flagConfig.enabled);
      setDescription(flagConfig.description || '');
      setPlatforms(flagConfig.platforms);
      setRegions(flagConfig.regions || []);
      setRolloutPercentage(flagConfig.rollout_percentage);
      setUserGroups(flagConfig.user_groups || []);
    }
  }, [flagName, flagConfig]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = t('featureFlags.flagEditor.flagNameError');
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) {
      newErrors.name = t('featureFlags.flagEditor.flagNameFormatError');
    } else if (!isEditing && existingFlags.includes(name)) {
      newErrors.name = t('featureFlags.flagEditor.flagNameExistsError');
    }

    if (platforms.length === 0) {
      newErrors.platforms = t('featureFlags.flagEditor.platformsError');
    }

    if (rolloutPercentage < 0 || rolloutPercentage > 100) {
      newErrors.rollout = t('featureFlags.flagEditor.rolloutError');
    }

    if (description.length > 500) {
      newErrors.description = t('featureFlags.flagEditor.descriptionLengthError');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const config: FeatureFlag = {
      enabled,
      description: description.trim() || undefined,
      platforms: platforms as ('ios' | 'android' | 'web')[],
      regions: regions.length > 0 ? (regions as ('cn' | 'global')[]) : undefined,
      rollout_percentage: rolloutPercentage,
      user_groups: userGroups.length > 0 ? userGroups : undefined,
    };

    onSave(name, config, !isEditing);
  };

  const addUserGroup = () => {
    const group = newGroupInput.trim().toLowerCase().replace(/\s+/g, '_');
    if (group && !userGroups.includes(group)) {
      setUserGroups([...userGroups, group]);
    }
    setNewGroupInput('');
  };

  const removeUserGroup = (group: string) => {
    setUserGroups(userGroups.filter((g) => g !== group));
  };

  const handleGroupInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addUserGroup();
    }
  };

  const togglePlatform = (platform: string) => {
    setPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const toggleRegion = (region: string) => {
    setRegions((prev) =>
      prev.includes(region)
        ? prev.filter((r) => r !== region)
        : [...prev, region]
    );
  };

  const getRegionLabel = (region: string) => {
    if (region === 'cn') return t('featureFlags.flagEditor.regionChina');
    if (region === 'global') return t('featureFlags.flagEditor.regionGlobal');
    return region;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? t('featureFlags.flagEditor.editTitle') : t('featureFlags.flagEditor.createTitle')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Flag Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.flagName')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isEditing}
              placeholder={t('featureFlags.flagEditor.flagNamePlaceholder')}
              className={`
                w-full px-3 py-2 border rounded-lg font-mono text-sm
                ${isEditing ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
                ${errors.name ? 'border-red-500' : 'border-gray-300'}
                focus:outline-none focus:ring-2 focus:ring-primary-500
              `}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('featureFlags.flagEditor.descriptionPlaceholder')}
              rows={2}
              className={`
                w-full px-3 py-2 border rounded-lg text-sm
                ${errors.description ? 'border-red-500' : 'border-gray-300'}
                focus:outline-none focus:ring-2 focus:ring-primary-500
              `}
            />
            <div className="flex justify-between mt-1">
              {errors.description ? (
                <p className="text-sm text-red-600">{errors.description}</p>
              ) : (
                <span />
              )}
              <span className="text-xs text-gray-400">
                {description.length}/500
              </span>
            </div>
          </div>

          {/* Enabled Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              {t('featureFlags.flagEditor.enabledByDefault')}
            </label>
            <button
              type="button"
              onClick={() => setEnabled(!enabled)}
              className={`
                relative w-11 h-6 rounded-full transition-colors
                ${enabled ? 'bg-green-500' : 'bg-gray-300'}
              `}
            >
              <span
                className={`
                  absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow
                  transition-transform
                  ${enabled ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>

          {/* Platforms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('featureFlags.flagEditor.platforms')}
            </label>
            <div className="flex gap-2">
              {PLATFORMS.map((platform) => (
                <button
                  key={platform}
                  type="button"
                  onClick={() => togglePlatform(platform)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${
                      platforms.includes(platform)
                        ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                    }
                  `}
                >
                  {platform}
                </button>
              ))}
            </div>
            {errors.platforms && (
              <p className="mt-1 text-sm text-red-600">{errors.platforms}</p>
            )}
          </div>

          {/* Regions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('featureFlags.flagEditor.regions')}
            </label>
            <p className="text-xs text-gray-500 mb-2">
              {t('featureFlags.flagEditor.regionsHelp')}
            </p>
            <div className="flex gap-2">
              {REGIONS.map((region) => (
                <button
                  key={region}
                  type="button"
                  onClick={() => toggleRegion(region)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${
                      regions.includes(region)
                        ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                    }
                  `}
                >
                  {getRegionLabel(region)}
                </button>
              ))}
            </div>
            {regions.length === 0 && (
              <p className="mt-1 text-xs text-gray-400">
                {t('featureFlags.flagEditor.regionsEmpty')}
              </p>
            )}
          </div>

          {/* Rollout Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.rolloutPercentage')}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                value={rolloutPercentage}
                onChange={(e) => setRolloutPercentage(Number(e.target.value))}
                className="flex-1"
              />
              <div className="w-16">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={rolloutPercentage}
                  onChange={(e) =>
                    setRolloutPercentage(
                      Math.min(100, Math.max(0, Number(e.target.value)))
                    )
                  }
                  className={`
                    w-full px-2 py-1 border rounded text-sm text-center
                    ${errors.rollout ? 'border-red-500' : 'border-gray-300'}
                    focus:outline-none focus:ring-2 focus:ring-primary-500
                  `}
                />
              </div>
              <span className="text-sm text-gray-500">%</span>
            </div>
            {errors.rollout && (
              <p className="mt-1 text-sm text-red-600">{errors.rollout}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {rolloutPercentage < 100
                ? t('featureFlags.flagEditor.rolloutPartialUsers', { percentage: String(rolloutPercentage) })
                : t('featureFlags.flagEditor.rolloutAllUsers')}
            </p>
          </div>

          {/* User Groups */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.targetUserGroups')}
            </label>
            <p className="text-xs text-gray-500 mb-2">
              {t('featureFlags.flagEditor.targetUserGroupsHelp')}
            </p>

            {/* Current groups */}
            {userGroups.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {userGroups.map((group) => (
                  <span
                    key={group}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                  >
                    {group}
                    <button
                      type="button"
                      onClick={() => removeUserGroup(group)}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add group input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newGroupInput}
                onChange={(e) => setNewGroupInput(e.target.value)}
                onKeyDown={handleGroupInputKeyDown}
                placeholder={t('featureFlags.flagEditor.addGroupPlaceholder')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm
                  focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={addUserGroup}
                disabled={!newGroupInput.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              {isEditing ? t('featureFlags.flagEditor.saveChanges') : t('featureFlags.flagEditor.createTitle')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
